import React from "react"
import { createRoot } from "react-dom/client"
import { ErrorBoundary } from "react-error-boundary"
import { asyncWithLDProvider } from "launchdarkly-react-client-sdk"

// core-js is a polyfill for various es6 and above features in IE11
// see : http://kangax.github.io/compat-table/es6/
// and: https://github.com/zloirock/core-js
import "core-js/stable"

import { initializeErrorReporting } from "utils/ErrorReporting"

import { BrowserRouter, Redirect, Switch } from "react-router-dom"
import { ExtendedStringifyOptions, QueryParamProvider, transformSearchStringJsonSafe } from "use-query-params"
import { Route } from "react-router"
import Employee from "pages/employees"
import Error404 from "pages/404"
import Error500 from "pages/500"
import { QueryClient, QueryClientProvider } from "react-query"
import styled from "@emotion/styled"
import { UserProvider } from "context/UserContext"
import Companies from "pages/companies"
import ChangeHealthcareLogsPage from "pages/change-healthcare-logs"
import Settings from "./utils/CarrotConfig"
import { AdminShellRedirector } from "AdminShellRedirector"
import { initializeHeap } from "services/heap/initialize"
import { CssBaseline, ThemeProvider } from "@carrotfertility/carotene-core"

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false
    }
  }
})

initializeErrorReporting()
initializeHeap()

const applicationNode = "body"

const queryStringifyOptions: ExtendedStringifyOptions = {
  transformSearchString: transformSearchStringJsonSafe
}

const Wrapper = styled.div`
  width: 990px;
  margin: 0 auto;
`

const Content = styled.div`
  margin-bottom: 30px;
`

;(async () => {
  const LDProvider = await asyncWithLDProvider({
    clientSideID: Settings.LAUNCH_DARKLY_CLIENT_ID
  })

  const container = document.getElementById(applicationNode)
  const root = createRoot(container)
  root.render(
    <LDProvider>
      <ErrorBoundary FallbackComponent={Error500}>
        <BrowserRouter>
          <QueryParamProvider ReactRouterRoute={Route} stringifyOptions={queryStringifyOptions}>
            <QueryClientProvider client={queryClient}>
              <UserProvider>
                <ThemeProvider>
                  <CssBaseline />
                  <AdminShellRedirector />
                  <Wrapper>
                    <Content>
                      <Switch>
                        <Route path="/employees/:employeeId" component={Employee} />
                        <Route path="/companies/" component={Companies} />
                        <Route path="/change-healthcare-logs" component={ChangeHealthcareLogsPage} />
                        <Route path="/404" component={Error404} />
                        <Route path="/500" component={Error500} />
                        <Redirect to="/404" />
                      </Switch>
                    </Content>
                  </Wrapper>
                </ThemeProvider>
              </UserProvider>
            </QueryClientProvider>
          </QueryParamProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </LDProvider>
  )
})()
